import type { AerodromeRef } from '@/lib/db';

/**
 * A bundled table of NZ aerodromes — `DATA-SOURCES.md` §4 — for
 * nearest-neighbour matching against a track's start/end fix so a drafted
 * logbook entry can propose "from"/"to" without a network lookup. Positions
 * are approximate (rounded to ~100m); good enough to find the right
 * aerodrome from a GPS fix a few hundred metres away, never good enough to
 * navigate by. A private strip or paddock with no ICAO code simply won't
 * match — the draft leaves `from`/`to` blank rather than guessing, and the
 * pilot fills it in.
 *
 * Covers every airport, aerodrome, heliport and seaplane base in NZ CAA's
 * ICAO-code range (`NZAA`–`NZZZ`) that OurAirports carries a position for,
 * so hospital and rig/platform helipads are in here alongside GA strips —
 * useful given `AircraftCategory` includes `'helicopter'`.
 */
export const NZ_AERODROMES: readonly AerodromeRef[] = [
  { icao: 'NZAA', name: 'Auckland', lat: -37.008, lon: 174.792 },
  { icao: 'NZWN', name: 'Wellington', lat: -41.327, lon: 174.805 },
  { icao: 'NZCH', name: 'Christchurch', lat: -43.489, lon: 172.532 },
  { icao: 'NZQN', name: 'Queenstown', lat: -45.021, lon: 168.739 },
  { icao: 'NZDN', name: 'Dunedin', lat: -45.928, lon: 170.198 },
  { icao: 'NZNP', name: 'New Plymouth', lat: -39.009, lon: 174.18 },
  { icao: 'NZNS', name: 'Nelson', lat: -41.298, lon: 173.221 },
  { icao: 'NZTG', name: 'Tauranga', lat: -37.672, lon: 176.196 },
  { icao: 'NZHN', name: 'Hamilton', lat: -37.867, lon: 175.332 },
  { icao: 'NZPM', name: 'Palmerston North', lat: -40.321, lon: 175.617 },
  { icao: 'NZWB', name: 'Woodbourne / Blenheim', lat: -41.518, lon: 173.869 },
  { icao: 'NZGS', name: 'Gisborne', lat: -38.663, lon: 177.978 },
  { icao: 'NZWU', name: 'Whanganui', lat: -39.963, lon: 175.025 },
  { icao: 'NZKT', name: 'Kaitaia', lat: -35.07, lon: 173.288 },
  { icao: 'NZWR', name: 'Whangarei', lat: -35.768, lon: 174.365 },
  { icao: 'NZAP', name: 'Taupo', lat: -38.74, lon: 176.084 },
  { icao: 'NZMS', name: 'Masterton (Hood)', lat: -40.973, lon: 175.641 },
  { icao: 'NZOU', name: 'Omarama', lat: -44.485, lon: 169.983 },
  { icao: 'NZNV', name: 'Invercargill', lat: -46.412, lon: 168.313 },
  { icao: 'NZRO', name: 'Rotorua', lat: -38.109, lon: 176.317 },
  { icao: 'NZWK', name: 'Whakatane', lat: -37.921, lon: 176.914 },
  { icao: 'NZMC', name: 'Mount Cook', lat: -43.765, lon: 170.133 },
  { icao: 'NZAR', name: 'Ardmore', lat: -37.03, lon: 174.972 },
  { icao: 'NZNE', name: 'North Shore', lat: -36.652, lon: 174.63 },
  { icao: 'NZNR', name: 'Napier (Hawke’s Bay)', lat: -39.466, lon: 176.87 },
  { icao: 'NZPP', name: 'Paraparaumu', lat: -40.905, lon: 174.989 },
  { icao: 'NZWF', name: 'Wanaka', lat: -44.727, lon: 169.246 },
  { icao: 'NZMO', name: 'Manapouri (Te Anau)', lat: -45.539, lon: 167.646 },
  { icao: 'NZKK', name: 'Kerikeri (Bay of Islands)', lat: -35.263, lon: 173.912 },
  { icao: 'NZOH', name: 'Ohakea', lat: -40.206, lon: 175.388 },
  { icao: 'NZTU', name: 'Timaru', lat: -44.303, lon: 171.225 },

  // Additional aerodromes, heliports and seaplane bases (source: OurAirports, Sep 2026).
  { icao: 'NZAE', name: 'Mount Tarawera', lat: -38.233, lon: 176.517 },
  { icao: 'NZAH', name: 'Taharoa Iron Sands Heliport', lat: -38.171, lon: 174.704 },
  { icao: 'NZAL', name: 'Avalon Heliport', lat: -41.187, lon: 174.944 },
  { icao: 'NZAN', name: 'Alfredton', lat: -40.698, lon: 175.856 },
  { icao: 'NZAS', name: 'Ashburton', lat: -43.903, lon: 171.797 },
  { icao: 'NZAW', name: 'Auckland Harbour Water Aerodrome', lat: -36.833, lon: 174.767 },
  { icao: 'NZBA', name: 'Balclutha', lat: -46.246, lon: 169.749 },
  { icao: 'NZBC', name: 'ASB Bank Centre Heliport', lat: -36.85, lon: 174.762 },
  { icao: 'NZBW', name: 'Burwood Hospital Heliport', lat: -43.48, lon: 172.687 },
  { icao: 'NZCB', name: 'Centre Bush', lat: -46.04, lon: 168.312 },
  { icao: 'NZCG', name: 'Centennial Park', lat: -38.665, lon: 176.135 },
  { icao: 'NZCI', name: 'Chatham Islands (Tuuta)', lat: -43.812, lon: -176.465 },
  { icao: 'NZCS', name: 'Cromwell Racecourse', lat: -45.049, lon: 169.171 },
  { icao: 'NZCX', name: 'Coromandel', lat: -36.792, lon: 175.509 },
  { icao: 'NZDA', name: 'Dargaville', lat: -35.939, lon: 173.893 },
  { icao: 'NZDC', name: 'Dunedin City Heliport', lat: -45.883, lon: 170.508 },
  { icao: 'NZDH', name: 'Dunedin Hospital Helipad', lat: -45.869, lon: 170.51 },
  { icao: 'NZDU', name: 'Dunstan Hospital Heliport', lat: -45.201, lon: 169.332 },
  { icao: 'NZDV', name: 'Dannevirke', lat: -40.232, lon: 176.079 },
  { icao: 'NZDY', name: 'Drury', lat: -37.096, lon: 174.975 },
  { icao: 'NZES', name: 'Wharepapa South', lat: -38.144, lon: 175.55 },
  { icao: 'NZEV', name: 'Eves Valley Heliport', lat: -41.34, lon: 173.078 },
  { icao: 'NZFA', name: 'Te Haupa Heliport', lat: -34.516, lon: 172.911 },
  { icao: 'NZFE', name: 'Fernside Fields', lat: -43.317, lon: 172.512 },
  { icao: 'NZFF', name: 'Forest Field', lat: -43.386, lon: 172.361 },
  { icao: 'NZFG', name: 'Fern Gully Heliport', lat: -46.895, lon: 168.111 },
  { icao: 'NZFH', name: 'Fox Heliport', lat: -43.463, lon: 170.0 },
  { icao: 'NZFI', name: 'Feilding', lat: -40.256, lon: 175.605 },
  { icao: 'NZFJ', name: 'Franz Josef', lat: -43.363, lon: 170.134 },
  { icao: 'NZFP', name: 'Foxpine', lat: -40.457, lon: 175.27 },
  { icao: 'NZFR', name: 'Franz Josef Heliport', lat: -43.377, lon: 170.194 },
  { icao: 'NZFT', name: 'Flat Point', lat: -41.243, lon: 175.96 },
  { icao: 'NZGA', name: 'Galatea', lat: -38.404, lon: 176.744 },
  { icao: 'NZGB', name: 'Great Barrier', lat: -36.241, lon: 175.472 },
  { icao: 'NZGC', name: 'Gore', lat: -46.157, lon: 168.898 },
  { icao: 'NZGH', name: 'Glacier Country Heliport', lat: -43.387, lon: 170.18 },
  { icao: 'NZGI', name: 'Garden City Heliport', lat: -43.493, lon: 172.548 },
  { icao: 'NZGM', name: 'Greymouth', lat: -42.462, lon: 171.19 },
  { icao: 'NZGR', name: 'Great Mercury', lat: -36.594, lon: 175.765 },
  { icao: 'NZGT', name: 'Glentanner', lat: -43.907, lon: 170.128 },
  { icao: 'NZGY', name: 'Glenorchy', lat: -44.872, lon: 168.398 },
  { icao: 'NZHA', name: 'Hawera', lat: -39.553, lon: 174.267 },
  { icao: 'NZHC', name: 'Wanaka Lakes Health Centre Heliport', lat: -44.709, lon: 169.137 },
  { icao: 'NZHK', name: 'Hokitika', lat: -42.714, lon: 170.985 },
  { icao: 'NZHR', name: 'Hanmer Springs Medical Centre Heliport', lat: -42.52, lon: 172.823 },
  { icao: 'NZHS', name: 'Hastings', lat: -39.647, lon: 176.767 },
  { icao: 'NZHT', name: 'Haast', lat: -43.865, lon: 169.041 },
  { icao: 'NZJA', name: 'Tauranga Hospital Heliport', lat: -37.706, lon: 176.149 },
  { icao: 'NZJB', name: 'Wairau Hospital Heliport', lat: -41.537, lon: 173.944 },
  { icao: 'NZJC', name: 'Christchurch Hospital Heliport', lat: -43.533, lon: 172.624 },
  { icao: 'NZJD', name: 'Thames Hospital Heliport', lat: -37.136, lon: 175.545 },
  { icao: 'NZJE', name: 'Dargaville Hospital Heliport', lat: -35.929, lon: 173.876 },
  { icao: 'NZJF', name: 'Whakatāne Hospital Helipad', lat: -37.966, lon: 176.977 },
  { icao: 'NZJG', name: 'Gisborne Hospital Heliport', lat: -38.641, lon: 178.004 },
  { icao: 'NZJH', name: 'Hastings Hospital Heliport', lat: -39.629, lon: 176.824 },
  { icao: 'NZJI', name: 'Bay of Islands Hospital Heliport', lat: -35.386, lon: 174.07 },
  { icao: 'NZJK', name: 'Kaitaia Hospital Heliport', lat: -35.118, lon: 173.261 },
  { icao: 'NZJL', name: 'Auckland Hospital Helipad', lat: -36.859, lon: 174.769 },
  { icao: 'NZJM', name: 'Palmerston North Hospital Heliport', lat: -40.338, lon: 175.619 },
  { icao: 'NZJN', name: 'North Shore Hospital Heliport', lat: -36.782, lon: 174.759 },
  { icao: 'NZJO', name: 'Rotorua Hospital Heliport', lat: -38.132, lon: 176.247 },
  { icao: 'NZJP', name: 'Te Puia Springs Hospital Heliport', lat: -38.055, lon: 178.304 },
  { icao: 'NZJQ', name: 'Taranaki Base Hospital Heliport', lat: -39.073, lon: 174.055 },
  { icao: 'NZJR', name: 'Whangarei Hospital Helipad', lat: -35.735, lon: 174.303 },
  { icao: 'NZJS', name: 'Southland - Kew Hospital Heliport', lat: -46.437, lon: 168.359 },
  { icao: 'NZJT', name: 'Taumarunui Hospital Heliport', lat: -38.89, lon: 175.251 },
  { icao: 'NZJU', name: 'Wanganui Hospital Heliport', lat: -39.944, lon: 175.039 },
  { icao: 'NZJV', name: 'Waitakere Hospital Heliport', lat: -36.871, lon: 174.631 },
  { icao: 'NZJW', name: 'Rawene Hospital Heliport', lat: -35.407, lon: 173.507 },
  { icao: 'NZJX', name: 'Tokoroa Hospital Heliport', lat: -38.232, lon: 175.861 },
  { icao: 'NZJY', name: 'Wairoa Hospital Heliport', lat: -39.042, lon: 177.414 },
  { icao: 'NZJZ', name: 'Taupo Hospital Heliport', lat: -38.699, lon: 176.099 },
  { icao: 'NZKC', name: 'Kelly Field', lat: -37.256, lon: 175.072 },
  { icao: 'NZKD', name: 'Motu Kaikoura Island', lat: -36.178, lon: 175.324 },
  { icao: 'NZKE', name: 'Waiheke Island', lat: -36.809, lon: 175.086 },
  { icao: 'NZKF', name: 'Kaipara Flats', lat: -36.406, lon: 174.587 },
  { icao: 'NZKH', name: 'Kenepuru Hospital Heliport', lat: -41.145, lon: 174.835 },
  { icao: 'NZKI', name: 'Kaikoura', lat: -42.425, lon: 173.605 },
  { icao: 'NZKM', name: 'Karamea', lat: -41.237, lon: 172.105 },
  { icao: 'NZKN', name: 'Kensington Park Heliport', lat: -35.705, lon: 174.313 },
  { icao: 'NZKO', name: 'Kaikohe', lat: -35.451, lon: 173.817 },
  { icao: 'NZKU', name: 'Kupe Helipad', lat: -39.8, lon: 174.117 },
  { icao: 'NZKY', name: 'Kowhai', lat: -39.846, lon: 176.423 },
  { icao: 'NZLD', name: 'Limestone Downs', lat: -37.48, lon: 174.745 },
  { icao: 'NZLE', name: 'Lake Station', lat: -41.759, lon: 172.746 },
  { icao: 'NZLF', name: 'Rotorua Lakefront Heliport', lat: -38.129, lon: 176.253 },
  { icao: 'NZLT', name: 'Lake Taupo Water Airport', lat: -38.732, lon: 176.035 },
  { icao: 'NZLX', name: 'Alexandra', lat: -45.21, lon: 169.371 },
  { icao: 'NZMA', name: 'Matamata Glider Airport', lat: -37.734, lon: 175.742 },
  { icao: 'NZMB', name: 'Mechanics Bay Heliport', lat: -36.847, lon: 174.788 },
  { icao: 'NZME', name: 'Mercer', lat: -37.258, lon: 175.115 },
  { icao: 'NZMF', name: 'Milford Sound', lat: -44.673, lon: 167.923 },
  { icao: 'NZMG', name: 'Mangonui Heliport', lat: -34.996, lon: 173.464 },
  { icao: 'NZMH', name: 'Wairarapa Hospital Heliport', lat: -40.947, lon: 175.675 },
  { icao: 'NZMJ', name: 'Martins Bay', lat: -44.366, lon: 168.016 },
  { icao: 'NZMK', name: 'Motueka', lat: -41.123, lon: 172.989 },
  { icao: 'NZML', name: 'Molesworth', lat: -42.083, lon: 173.283 },
  { icao: 'NZMQ', name: 'Oaonui Heliport', lat: -39.4, lon: 173.81 },
  { icao: 'NZMR', name: 'Murchison', lat: -41.797, lon: 172.315 },
  { icao: 'NZMT', name: 'Martinborough', lat: -41.214, lon: 175.486 },
  { icao: 'NZMV', name: 'Methven Heliport', lat: -43.635, lon: 171.654 },
  { icao: 'NZMW', name: 'Makarora', lat: -44.232, lon: 169.23 },
  { icao: 'NZMX', name: 'Maari Heliport', lat: -39.975, lon: 173.292 },
  { icao: 'NZMZ', name: 'Matakana Island', lat: -37.6, lon: 176.083 },
  { icao: 'NZNF', name: 'Norfolk', lat: -39.205, lon: 174.223 },
  { icao: 'NZNH', name: 'Nelson Hospital Heliport', lat: -41.288, lon: 173.272 },
  { icao: 'NZOA', name: 'Omarama Glider Airport', lat: -44.487, lon: 169.986 },
  { icao: 'NZOB', name: 'Ocean Beach Heliport', lat: -46.589, lon: 168.306 },
  { icao: 'NZOF', name: 'Omaha Flats', lat: -36.347, lon: 174.759 },
  { icao: 'NZOG', name: 'Makarora Heliport', lat: -44.233, lon: 169.23 },
  { icao: 'NZOI', name: 'Motiti Island', lat: -37.633, lon: 176.417 },
  { icao: 'NZOM', name: 'Omaka Blenheim', lat: -41.54, lon: 173.922 },
  { icao: 'NZON', name: 'Oban Heliport', lat: -46.901, lon: 168.124 },
  { icao: 'NZOP', name: 'Opotiki', lat: -38.022, lon: 177.307 },
  { icao: 'NZOR', name: 'Oamaru Hospital Heliport', lat: -45.099, lon: 170.967 },
  { icao: 'NZOS', name: 'Otehei Bay Helibarge', lat: -35.222, lon: 174.231 },
  { icao: 'NZOT', name: 'Otaki', lat: -40.786, lon: 175.151 },
  { icao: 'NZOW', name: 'Onetangi Heliport', lat: -36.805, lon: 175.071 },
  { icao: 'NZOX', name: 'Okiwi Station', lat: -36.146, lon: 175.419 },
  { icao: 'NZPA', name: 'Paihia HEMS Heliport', lat: -35.284, lon: 174.09 },
  { icao: 'NZPH', name: 'Pudding Hill', lat: -43.594, lon: 171.531 },
  { icao: 'NZPI', name: 'Parakai', lat: -36.652, lon: 174.436 },
  { icao: 'NZPK', name: 'Pikes Point Heliport', lat: -36.93, lon: 174.813 },
  { icao: 'NZPN', name: 'Picton', lat: -41.346, lon: 173.956 },
  { icao: 'NZPO', name: 'Pōrangahau', lat: -40.279, lon: 176.65 },
  { icao: 'NZPS', name: 'Paihia Waterfront Heliport', lat: -35.282, lon: 174.093 },
  { icao: 'NZPW', name: 'Papawai', lat: -41.104, lon: 175.5 },
  { icao: 'NZQW', name: 'Queens Wharf Heliport', lat: -41.286, lon: 174.78 },
  { icao: 'NZRA', name: 'Raglan', lat: -37.805, lon: 174.86 },
  { icao: 'NZRC', name: 'Ryan’s Creek', lat: -46.9, lon: 168.101 },
  { icao: 'NZRD', name: 'Rosedale Heliport', lat: -36.742, lon: 174.716 },
  { icao: 'NZRI', name: 'Rangitata Island', lat: -44.085, lon: 171.416 },
  { icao: 'NZRK', name: 'Rangitaiki', lat: -38.886, lon: 176.364 },
  { icao: 'NZRL', name: 'Rotorua Lakes Water Aerodrome', lat: -38.083, lon: 176.267 },
  { icao: 'NZRN', name: 'Ranfurly Medical Centre Heliport', lat: -45.13, lon: 170.111 },
  { icao: 'NZRR', name: 'Ruatoria', lat: -37.883, lon: 178.29 },
  { icao: 'NZRT', name: 'Rangiora', lat: -43.29, lon: 172.542 },
  { icao: 'NZRU', name: 'Waiouru', lat: -39.446, lon: 175.658 },
  { icao: 'NZRW', name: 'Ruawai', lat: -36.097, lon: 173.977 },
  { icao: 'NZRX', name: 'Roxburgh', lat: -45.512, lon: 169.317 },
  { icao: 'NZSD', name: 'Stratford', lat: -39.319, lon: 174.31 },
  { icao: 'NZSF', name: 'Springfield', lat: -43.384, lon: 171.911 },
  { icao: 'NZSL', name: 'Springhill', lat: -36.324, lon: 174.556 },
  { icao: 'NZSO', name: 'Marlborough Sounds Water Aerodrome', lat: -41.009, lon: 174.089 },
  { icao: 'NZTA', name: 'Te Aroha', lat: -37.475, lon: 175.618 },
  { icao: 'NZTC', name: 'Tapanui Medical Centre Helipad', lat: -45.938, lon: 169.263 },
  { icao: 'NZTE', name: 'Te Kowhai', lat: -37.744, lon: 175.161 },
  { icao: 'NZTH', name: 'Thames', lat: -37.157, lon: 175.55 },
  { icao: 'NZTI', name: 'Taieri', lat: -45.861, lon: 170.363 },
  { icao: 'NZTK', name: 'Takaka', lat: -40.813, lon: 172.775 },
  { icao: 'NZTL', name: 'Tekapo', lat: -44.005, lon: 170.444 },
  { icao: 'NZTM', name: 'Taumarunui', lat: -38.839, lon: 175.262 },
  { icao: 'NZTN', name: 'Turangi', lat: -38.968, lon: 175.814 },
  { icao: 'NZTO', name: 'Tokoroa', lat: -38.237, lon: 175.892 },
  { icao: 'NZTP', name: 'Tekapo/Mackenzie Heliport', lat: -44.013, lon: 170.407 },
  { icao: 'NZTQ', name: 'Te Kuiti Hospital Heliport', lat: -38.334, lon: 175.152 },
  { icao: 'NZTT', name: 'Te Kuiti', lat: -38.304, lon: 175.148 },
  { icao: 'NZTW', name: 'Twizel Medical Centre Helipad', lat: -44.261, lon: 170.098 },
  { icao: 'NZTX', name: 'Te Anau Medical Centre Heliport', lat: -45.416, lon: 167.72 },
  { icao: 'NZTZ', name: 'Timaru Hospital Helipad', lat: -44.409, lon: 171.256 },
  { icao: 'NZUA', name: 'Maui A Heliport', lat: -39.558, lon: 173.45 },
  { icao: 'NZUB', name: 'Maui B Heliport', lat: -39.65, lon: 173.308 },
  { icao: 'NZUI', name: 'Tui Helipad', lat: -39.432, lon: 173.233 },
  { icao: 'NZUK', name: 'Pukaki', lat: -44.235, lon: 170.118 },
  { icao: 'NZUN', name: 'Pauanui', lat: -37.021, lon: 175.864 },
  { icao: 'NZUR', name: 'Murchison Hospital Heliport', lat: -41.807, lon: 172.328 },
  { icao: 'NZVL', name: 'Mandeville', lat: -45.99, lon: 168.812 },
  { icao: 'NZVR', name: 'Taihape', lat: -39.685, lon: 175.789 },
  { icao: 'NZWE', name: 'Kauaroa Bay Heliport', lat: -36.83, lon: 175.066 },
  { icao: 'NZWG', name: 'Whangamata Helipad', lat: -37.201, lon: 175.867 },
  { icao: 'NZWH', name: 'Wellington Hospital Helistop', lat: -41.309, lon: 174.779 },
  { icao: 'NZWJ', name: 'Wellsford Heliport', lat: -36.298, lon: 174.523 },
  { icao: 'NZWL', name: 'West Melton', lat: -43.477, lon: 172.393 },
  { icao: 'NZWM', name: 'Waimate', lat: -44.79, lon: 171.09 },
  { icao: 'NZWO', name: 'Wairoa', lat: -39.012, lon: 177.404 },
  { icao: 'NZWP', name: 'Whenuapai (RNZAF)', lat: -36.788, lon: 174.63 },
  { icao: 'NZWQ', name: 'Waitiki', lat: -34.527, lon: 172.838 },
  { icao: 'NZWS', name: 'Westport', lat: -41.737, lon: 171.579 },
  { icao: 'NZWT', name: 'Whitianga', lat: -36.829, lon: 175.683 },
  { icao: 'NZWV', name: 'Waihi Beach', lat: -37.43, lon: 175.952 },
  { icao: 'NZWZ', name: 'Warkworth Heliport', lat: -36.398, lon: 174.659 },
  { icao: 'NZYP', name: 'Waipukurau', lat: -39.997, lon: 176.537 },
];
